import { create } from 'zustand';

import { type Command } from '../types';

const MAX_HISTORY_SIZE = 50;

type StackSnapshot = { redoStack: Command[]; undoStack: Command[] };

type CommandHistoryState = {
  redoStack: Command[];
  savedStacks: StackSnapshot[];
  undoStack: Command[];
};

type CommandHistoryActions = {
  clear: () => void;
  execute: (command: Command) => void;
  popStack: () => void;
  push: (command: Command) => void;
  pushStack: () => void;
  redo: () => void;
  undo: () => void;
};

export const useCommandHistory = create<CommandHistoryState & CommandHistoryActions>((set, get) => ({
  clear: () => set({ redoStack: [], savedStacks: [], undoStack: [] }),

  execute: (command) => {
    command.execute();
    set((state) => ({
      redoStack: [],
      undoStack: [...state.undoStack, command].slice(-MAX_HISTORY_SIZE),
    }));
  },

  popStack: () =>
    set((state) => {
      const restored = state.savedStacks[state.savedStacks.length - 1];

      if (!restored) {
        return state;
      }

      return {
        redoStack: restored.redoStack,
        savedStacks: state.savedStacks.slice(0, -1),
        undoStack: restored.undoStack,
      };
    }),

  push: (command) => {
    set((state) => ({
      redoStack: [],
      undoStack: [...state.undoStack, command].slice(-MAX_HISTORY_SIZE),
    }));
  },

  pushStack: () =>
    set((state) => ({
      redoStack: [],
      savedStacks: [...state.savedStacks, { redoStack: state.redoStack, undoStack: state.undoStack }],
      undoStack: [],
    })),

  redo: () => {
    const { redoStack } = get();
    const command = redoStack[redoStack.length - 1];

    if (!command) {
      return;
    }

    command.execute();
    set((state) => ({
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, command],
    }));
  },
  redoStack: [],
  savedStacks: [],

  undo: () => {
    const { undoStack } = get();
    const command = undoStack[undoStack.length - 1];

    if (!command) {
      return;
    }

    command.undo();
    set((state) => ({
      redoStack: [...state.redoStack, command],
      undoStack: state.undoStack.slice(0, -1),
    }));
  },
  undoStack: [],
}));
